import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { integrationService } from '../services/api';
import {
  Home, Compass, FileText, Settings as SettingsIcon, LayoutDashboard, Magnet,
  TrendingUp, CheckSquare, Gauge, Users, ClipboardList, Briefcase, UserCheck,
  ScrollText, Calculator, Layers, MessageSquare, Truck, Package, Landmark,
  History, BarChart3, HardDrive, Shield, Cog, LayoutGrid,
} from 'lucide-react';

const MobileNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [integrationOn, setIntegrationOn] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const isAdmin = ['admin', 'super_admin', 'ops_admin', 'safety_compliance_admin', 'payroll_admin'].includes(user?.role);
  const superOrOps = user?.role === 'super_admin' || user?.role === 'ops_admin';

  useEffect(() => {
    let active = true;
    integrationService.isEnabled().then((on) => { if (active) setIntegrationOn(on); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const commonTop = [
    { path: '/dashboard', label: 'Home', icon: Home },
    { path: '/hub', label: 'Company Hub', icon: Compass },
    ...(!isAdmin ? [{ path: '/my-documents', label: 'My Documents', icon: FileText }] : []),
    { path: '/settings', label: 'Settings', icon: SettingsIcon },
  ];

  const adminSections = [
    { title: 'Attention', icon: Gauge, items: [
      { path: '/admin/attention', label: 'Founder Attention', icon: LayoutDashboard },
    ]},
    { title: 'Operations', icon: Truck, items: [
      { path: '/admin/loads', label: 'Jobs / Loads', icon: Package },
      { path: '/admin/quote-builder', label: 'Quote Builder', icon: Calculator },
      { path: '/admin/dispatch', label: 'Dispatch', icon: Truck },
      { path: '/admin/dashboard', label: 'Driver Operations', icon: LayoutDashboard },
      { path: '/operations/capability-check', label: 'Equipment Capability', icon: CheckSquare },
      { path: '/operations/history', label: 'Operational History', icon: History },
    ]},
    { title: 'Business', icon: Briefcase, items: [
      { path: '/admin/leads', label: 'Customers & Leads', icon: Magnet },
      { path: '/admin/partner-requests', label: 'Partner Requests', icon: Package },
      { path: '/admin/partners', label: 'Partner Accounts', icon: Users },
      ...(integrationOn ? [{ path: '/admin/customer-360', label: 'Customer 360', icon: Layers }] : []),
      { path: '/admin/sales-intelligence', label: 'Sales', icon: TrendingUp },
      { path: '/admin/communications', label: 'Outreach / Communications', icon: MessageSquare },
    ]},
    { title: 'People', icon: Users, items: [
      { path: '/admin/people', label: 'People & Companies', icon: Users },
      { path: '/applicants', label: 'Applicants', icon: Users },
      { path: '/operations', label: 'Applicant Intelligence', icon: BarChart3 },
      { path: '/onboarding', label: 'Driver Onboarding', icon: ClipboardList },
      { path: '/admin/staff-onboarding', label: 'Staff Setup', icon: UserCheck },
      ...(superOrOps ? [{ path: '/admin/staff-directory', label: 'Staff Directory', icon: Users }] : []),
      { path: '/admin/jobs', label: 'Recruiting / Job Drafts', icon: Briefcase },
      { path: '/admin/activities', label: 'Follow-Up Activities', icon: CheckSquare },
    ]},
    { title: 'Finance', icon: Landmark, items: [
      { path: '/admin/accounting', label: 'Settlements', icon: Landmark },
    ]},
    { title: 'Compliance & Documents', icon: FileText, items: [
      { path: '/admin/documents', label: 'Documents', icon: FileText },
    ]},
    { title: 'Admin & System', icon: Shield, items: [
      { path: '/admin/users', label: 'All Users', icon: LayoutGrid },
      ...(integrationOn ? [{ path: '/admin/integration', label: 'Integration Console', icon: HardDrive }] : []),
      { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
      { path: '/admin/audit-logs', label: 'Audit Logs', icon: ScrollText },
      { path: '/admin/activation-status', label: 'Activation Status', icon: Gauge },
      ...(superOrOps ? [
        { path: '/admin/feature-toggles', label: 'Feature Controls', icon: Cog },
        { path: '/admin/site-content', label: 'Site Content', icon: FileText },
        { path: '/admin/user-activity', label: 'User Activity', icon: History },
      ] : []),
      ...(user?.role === 'super_admin' ? [
        { path: '/admin/founder-maintenance', label: 'Founder Maintenance', icon: Shield },
      ] : []),
    ]},
  ];

  const isActive = (path) => location.pathname === path;

  const renderItem = (item) => (
    <Link
      key={item.path + item.label}
      to={item.path}
      onClick={() => setIsOpen(false)}
      data-testid={`mobilenav-${item.path.replace(/\//g, '-')}`}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
        isActive(item.path) ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      <item.icon className="w-4 h-4" />
      <span className="text-sm font-medium">{item.label}</span>
    </Link>
  );

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
        aria-label="Toggle menu"
        data-testid="mobile-menu-btn"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
        </svg>
      </button>

      {isOpen && <div className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setIsOpen(false)} />}

      <div className={`lg:hidden fixed inset-y-0 right-0 z-50 w-72 bg-white shadow-xl transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="px-4 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-900">{user?.first_name} {user?.last_name}</span>
              <button onClick={() => setIsOpen(false)} className="p-2 text-gray-500 hover:text-gray-700" aria-label="Close menu">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-1 truncate">{user?.display_role_label || user?.email}</p>
          </div>

          <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
            {commonTop.map(renderItem)}
            {isAdmin && adminSections.map((section) => (
              <div key={section.title} className="pt-3">
                <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
                  <section.icon className="w-3.5 h-3.5" /> {section.title}
                </p>
                {section.items.map(renderItem)}
              </div>
            ))}
          </nav>

          <div className="px-4 py-4 border-t border-gray-200">
            <button
              onClick={() => { logout(); setIsOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              data-testid="mobile-logout-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileNav;
